import { motion, useInView } from "framer-motion";
import { useRef, useMemo } from "react";

/**
 * Scroll-triggered statement headline with per-word stagger reveal.
 *
 * Props:
 *   - overline: small label above
 *   - words:   array of { text, accent? } — accent=true italicizes + neon-colors that word
 *              OR a string where *word* marks an accent word.
 *   - period:  show a punctuation period at end (true by default)
 */
export default function Manifesto({
  overline = "Our Belief",
  words = [],
  period = true,
  className = "",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  // Allow `words` to be a string. Tokens wrapped in *asterisks* become accent words.
  const parsed = useMemo(() => {
    if (Array.isArray(words)) return words;
    if (typeof words !== "string" || !words.trim()) return [];
    return words
      .trim()
      .split(/\s+/)
      .map((tok) => {
        const accent = tok.startsWith("*") && tok.endsWith("*") && tok.length > 2;
        const text = accent ? tok.slice(1, -1) : tok;
        return { text, accent };
      });
  }, [words]);

  return (
    <section ref={ref} className={`section-y bg-emerald-ink text-bone relative overflow-hidden ${className}`}>
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="overline text-neon mb-8 flex items-center gap-3"
        >
          <span className="live-dot" />
          {overline}
        </motion.div>

        <h2 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7rem] leading-[0.95] tracking-tight">
          {parsed.map((w, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
              animate={inView ? { opacity: 1, y: 0, filter: "blur(0)" } : {}}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`inline-block mr-3 ${w.accent ? "accent-italic" : "gradient-text"}`}
            >
              {w.text}
            </motion.span>
          ))}
          {period && parsed.length > 0 && (
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: parsed.length * 0.08 + 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block text-neon"
            >
              .
            </motion.span>
          )}
        </h2>
      </div>
    </section>
  );
}
